import { useState, useEffect } from "react";
import { C, Sheet, Btn, Badge, Stat, Topbar, BottomNav, Divider, Label } from "../components/shared";

const CHECKOUT_URL = "https://quotemybodywork-api.vercel.app/api/createCheckoutSession";
const CONFIRMPURCHASE_URL = "https://quotemybodywork-api.vercel.app/api/confirmPurchase";

export default function BodyshopPortal({ leads, shopId, userProfile, onBuyLead, onLogout }) {
  const [nav, setNav] = useState("qualified");
  const [bsTab, setBsTab] = useState("available");
  const [sheet, setSheet] = useState(null);
  const [buying, setBuying] = useState(false);

  const myBought = leads.filter(l => l.buyers?.includes(shopId));
  const available = leads.filter(l => l.status !== "closed" && !l.buyers?.includes(shopId) && (l.buyers?.length || 0) < l.maxBuyers);
  const totalSpent = myBought.reduce((s,l) => s + (l.price||0), 0);
  const [paymentSheet, setPaymentSheet] = useState(null);

  const isToday = (lead) => {
    const d = lead.createdAt?.toDate ? lead.createdAt.toDate() : lead.createdAt ? new Date(lead.createdAt) : null;
    if (!d) return false;
    const now = new Date();
    const ukNow = new Date(now.toLocaleString("en-GB", {timeZone:"Europe/London"}));
    const ukLead = new Date(d.toLocaleString("en-GB", {timeZone:"Europe/London"}));
    return ukNow.toDateString() === ukLead.toDateString();
  };

  const sortNewest = arr => [...arr].sort((a,b)=>{
    const ta = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt ? new Date(a.createdAt) : new Date(0);
    const tb = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt ? new Date(b.createdAt) : new Date(0);
    return tb - ta;
  });

  const tierAvail = {
    premium: sortNewest(available.filter(l=>l.tier==="premium")),
    qualified: sortNewest(available.filter(l=>l.tier==="qualified"||!l.tier)),
    bronze: sortNewest(available.filter(l=>l.tier==="bronze")),
  };
  const tierBought = {
    premium: sortNewest(myBought.filter(l=>l.tier==="premium")),
    qualified: sortNewest(myBought.filter(l=>l.tier==="qualified"||!l.tier)),
    bronze: sortNewest(myBought.filter(l=>l.tier==="bronze")),
  };

  const doBuy = async (lead) => {
    setBuying(true);
    try {
      const res = await fetch(CHECKOUT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          leadId: lead.id,
          shopId,
          amount: lead.price,
          leadName: `${lead.vehicle} — ${lead.area}`,
          returnUrl: window.location.href,
        }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch(e) { throw new Error("Bad response: " + text.slice(0,200)); }
      if (!res.ok || data.error) throw new Error(data.error || `Server error: ${res.status}`);
      if (!data.url) throw new Error("No checkout URL returned");
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (e) {
      alert("Payment failed: " + e.message);
      setBuying(false);
    }
  };

  // Handle return from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("success");
    const sessionId = params.get("session_id");
    const leadId = params.get("leadId");
    const shopIdParam = params.get("shopId");
    if (success && sessionId) {
      window.history.replaceState({}, "", window.location.pathname);
      fetch(CONFIRMPURCHASE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId }),
      }).then(r => r.json()).then(() => {
        alert("✅ Payment successful! The lead is now unlocked in \"My Purchases\".");
      }).catch(() => {
        alert("Payment went through but lead unlock failed. Contact support.");
      });
    }
    if (params.get("cancelled")) {
      window.history.replaceState({}, "", window.location.pathname);
      alert("Payment cancelled.");
    }
  }, []);

  return (
    <div style={{ minHeight:"100vh", background:C.dark, paddingBottom:80, color:C.text, fontFamily:"'DM Sans',sans-serif" }}>
      <Topbar right={<div style={{ background:"rgba(59,130,246,0.1)", border:"1px solid rgba(59,130,246,0.3)", color:C.blue, fontSize:11, padding:"3px 10px", borderRadius:20, fontWeight:700, maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>🏭 {userProfile?.businessName}</div>} />


      {(nav==="premium"||nav==="qualified"||nav==="bronze") && (()=>{
        const currentAvail = tierAvail[nav];
        const currentBought = tierBought[nav];
        const tierLabel = nav==="premium" ? "⭐ Verified Premium" : nav==="qualified" ? "🏠 Qualified" : "🥉 Bronze";
        const tierColor = nav==="premium" ? "#f59e0b" : nav==="qualified" ? C.blue : "#92400e";

        const AvailCard = (lead) => (
          <div key={lead.id} onClick={()=>setSheet({type:"available",lead})} style={{ background:C.card, border:`1px solid ${lead.budget?.includes("1,000")||lead.budget?.includes("5,000")?"#3a2a0a":C.border}`, borderRadius:14, marginBottom:12, overflow:"hidden", cursor:"pointer" }}>
            <div style={{ padding:"14px 16px", display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:15 }}>{lead.vehicle} — {lead.area} area</div>
                <div style={{ fontSize:12, color:C.muted, marginTop:3 }}>{lead.workType} · {lead.budget}</div>
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {isToday(lead)&&<Badge color="green">● New</Badge>}
                {(lead.budget?.includes("1,000")||lead.budget?.includes("5,000"))&&<Badge color="red">🔥 Hot</Badge>}
                {lead.tier==="bronze"&&<Badge color="amber">🥉 Bronze</Badge>}
                {lead.tier==="qualified"&&<Badge color="blue">🏠 Qualified</Badge>}
                {lead.tier==="premium"&&<Badge color="green">⭐ Premium</Badge>}
              </div>
            </div>
            <div style={{ padding:"0 16px 14px" }}>
              <div style={{ fontSize:13, color:"#AAAAAA", lineHeight:1.6, marginBottom:10 }}>{(lead.damage||"").slice(0,100)}{(lead.damage||"").length>100?"...":""}</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {lead.photoCount>0&&<span style={{ background:"#1a1a1a", border:`1px solid ${C.border}`, borderRadius:6, padding:"3px 8px", fontSize:11, color:C.muted }}>📷 {lead.photoCount} photos</span>}
                <span style={{ background:"#1a1a1a", border:`1px solid ${C.border}`, borderRadius:6, padding:"3px 8px", fontSize:11, color:(lead.maxBuyers-(lead.buyers?.length||0))===1?C.amber:C.muted }}>
                  {(lead.maxBuyers-(lead.buyers?.length||0))===1?"⚡ Last slot!":`${lead.maxBuyers-(lead.buyers?.length||0)} slots left`}
                </span>
              </div>
            </div>
            <div style={{ padding:"12px 16px", borderTop:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontSize:18, fontWeight:900, color:C.greenLight }}>£{lead.price}</span>
              <button onClick={e=>{e.stopPropagation();setSheet({type:"available",lead});}} style={{ background:C.red, color:"#fff", border:"none", borderRadius:10, padding:"9px 18px", fontFamily:"inherit", fontSize:13, fontWeight:700, cursor:"pointer" }}>Buy lead</button>
            </div>
          </div>
        );

        const BoughtCard = (lead) => {
          const d = lead.createdAt?.toDate ? lead.createdAt.toDate() : lead.createdAt ? new Date(lead.createdAt) : null;
          const dateStr = d ? d.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : "";
          return (
            <div key={lead.id} onClick={()=>setSheet({type:"purchased",lead})} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, marginBottom:12, overflow:"hidden", cursor:"pointer" }}>
              <div style={{ padding:"14px 16px", display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8 }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:15 }}>{lead.name} · {lead.vehicle}</div>
                  <div style={{ fontSize:12, color:C.muted, marginTop:3 }}>{lead.budget} · {dateStr}</div>
                </div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  <Badge color="blue">✓ Bought</Badge>
                  {(lead.budget?.includes("1,000")||lead.budget?.includes("5,000"))&&<Badge color="red">🔥 Hot</Badge>}
                  {lead.tier==="bronze"&&<Badge color="amber">🥉 Bronze</Badge>}
                  {lead.tier==="qualified"&&<Badge color="blue">🏠 Qualified</Badge>}
                  {lead.tier==="premium"&&<Badge color="green">⭐ Premium</Badge>}
                </div>
              </div>
              {lead.adminNote&&<div style={{ padding:"0 16px 10px", fontSize:12, color:C.text }}>💬 {lead.adminNote}</div>}
              <div style={{ padding:"0 16px 14px" }}><div style={{ fontSize:13, color:"#AAAAAA", lineHeight:1.6 }}>{(lead.damage||"").slice(0,80)}...</div></div>
              <div style={{ padding:"12px 16px", borderTop:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ fontSize:13, color:C.greenLight, fontWeight:700 }}>📞 {lead.phone}</div>
                <Btn size="sm" variant="ghost" style={{ color:C.greenLight, borderColor:"rgba(34,197,94,0.3)", background:"rgba(22,163,74,0.08)" }}>View details</Btn>
              </div>
            </div>
          );
        };

        return (
          <div style={{ padding:16 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16, marginTop:4 }}>
              <Stat label="Available" value={currentAvail.length} sub={tierLabel} accent={tierColor} />
              <Stat label="Purchased" value={currentBought.length} sub="This tier" accent={C.greenLight} />
              <Stat label="Total spent" value={`£${totalSpent}`} sub="All tiers" accent={C.amber} />
              <Stat label="Radius" value={`${userProfile?.radius||25}mi`} sub={userProfile?.postcode||""} accent={C.blue} />
            </div>
            <div style={{ display:"flex", gap:8, marginBottom:16 }}>
              {[["available",`Available (${currentAvail.length})`],["purchased",`My Purchases (${currentBought.length})`]].map(([id,lbl])=>(
                <div key={id} onClick={()=>setBsTab(id)} style={{ padding:"6px 14px", borderRadius:20, fontSize:12, fontWeight:700, cursor:"pointer", border:`1px solid ${bsTab===id?C.red:C.border}`, background:bsTab===id?C.red:C.card, color:bsTab===id?"#fff":C.muted }}>{lbl}</div>
              ))}
            </div>
            {bsTab==="available"&&(currentAvail.length===0
              ? <div style={{ textAlign:"center", padding:"40px 20px", color:C.muted, fontSize:14 }}>No {tierLabel} leads available right now.<br/><br/>Check back soon.</div>
              : currentAvail.map(AvailCard)
            )}
            {bsTab==="purchased"&&(currentBought.length===0
              ? <div style={{ textAlign:"center", padding:"40px 20px", color:C.muted, fontSize:14 }}>No purchased {tierLabel} leads yet.</div>
              : currentBought.map(BoughtCard)
            )}
          </div>
        );
      })()}

      {nav === "account" && (
        <div style={{ padding:16 }}>
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:18, marginBottom:12 }}>
            <div style={{ width:52, height:52, borderRadius:"50%", background:"rgba(232,52,26,0.15)", border:"1px solid rgba(232,52,26,0.3)", color:C.red, fontWeight:800, fontSize:18, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:14 }}>
              {(userProfile?.businessName||"").slice(0,2).toUpperCase()}
            </div>
            <div style={{ fontSize:18, fontWeight:800 }}>{userProfile?.businessName}</div>
            <div style={{ fontSize:13, color:C.muted, marginTop:4 }}>{userProfile?.postcode} · <span style={{ color:C.greenLight }}>✓ Verified</span></div>
          </div>
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:16, marginBottom:12 }}>
            {[["Account status",<span style={{ color:C.greenLight, fontWeight:700 }}>● Active</span>],["Lead radius",`${userProfile?.radius||25} miles from ${userProfile?.postcode||""}`],["Leads purchased",`${myBought.length} total`],["Total spent",`£${totalSpent}`]].map(([k,v])=>(
              <div key={k} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"11px 0", borderBottom:`1px solid ${C.border}`, fontSize:13 }}>
                <div style={{ color:C.muted }}>{k}</div><div style={{ color:C.text }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ background:"#0D0D0D", border:`1px solid ${C.border}`, borderRadius:14, padding:14, marginBottom:12, fontSize:13, color:C.muted, lineHeight:1.7, textAlign:"center" }}>
            To update your details contact<br /><span style={{ color:C.red }}>support@quotemybodywork.co.uk</span>
          </div>
          <Btn onClick={onLogout} variant="ghost" size="lg" full>Sign out</Btn>
        </div>
      )}

      <BottomNav active={nav} onSelect={n=>{ setNav(n); setBsTab("available"); }} tabs={[
        { id:"premium", icon:"⭐", label:"Premium", count:tierAvail.premium.length },
        { id:"qualified", icon:"🏠", label:"Qualified", count:tierAvail.qualified.length },
        { id:"bronze", icon:"🥉", label:"Bronze", count:tierAvail.bronze.length },
        { id:"account", icon:"🏭", label:"Account", count:0 },
      ]} />

      {/* Available lead sheet */}
      <Sheet open={sheet?.type==="available"} onClose={()=>setSheet(null)}>
        {sheet?.type==="available"&&(()=>{
          const l=sheet.lead;
          const slotsLeft=l.maxBuyers-(l.buyers?.length||0);
          return (<>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
              <div style={{ fontSize:19, fontWeight:900 }}>{l.vehicle}</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                <Badge color="green">● New</Badge>
                {(l.budget?.includes("1,000")||l.budget?.includes("5,000"))&&<Badge color="red">🔥 Hot</Badge>}
                {l.tier==="bronze"&&<Badge color="amber">🥉 Bronze</Badge>}
                {l.tier==="qualified"&&<Badge color="blue">🏠 Qualified</Badge>}
                {l.tier==="premium"&&<Badge color="green">⭐ Verified Premium</Badge>}
              </div>
            </div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:l.adminNote?12:20 }}>{l.area} area · Submitted recently</div>
            {l.adminNote&&<div style={{ background:"rgba(59,130,246,0.08)", border:"1px solid rgba(59,130,246,0.25)", borderRadius:10, padding:"10px 14px", marginBottom:16, fontSize:13, color:C.text, lineHeight:1.6 }}>💬 <strong>Expert note:</strong> {l.adminNote}</div>}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
              {[["Vehicle",l.vehicle],["Budget",l.budget],["Work type",l.workType],["Quoted elsewhere?",l.quotedElsewhere],["Best call time",l.callTime],["Photos",l.photoCount>0?`${l.photoCount} uploaded`:"None yet"]].map(([k,v])=>(
                <div key={k}><Label>{k}</Label><div style={{ fontSize:13, color:C.text }}>{v}</div></div>
              ))}
              <div style={{ gridColumn:"1/-1" }}><Label>Damage description</Label><div style={{ fontSize:13, color:C.text, lineHeight:1.6 }}>{l.damage}</div></div>
            </div>
            {l.photoUrls?.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <Label>Photos</Label>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
                  {l.photoUrls.map((url,i)=>(<div key={i} style={{ aspectRatio:1, borderRadius:10, overflow:"hidden", border:`1px solid ${C.border}` }}><img src={url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /></div>))}
                </div>
              </div>
            )}
            <Divider />
            {slotsLeft===1&&<div style={{ fontSize:12, color:C.amber, background:"rgba(245,158,11,0.1)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:8, padding:"7px 12px", display:"inline-block", marginBottom:14, fontWeight:600 }}>⚡ Only 1 slot remaining on this lead</div>}
            <div style={{ background:"#0D0D0D", border:`1.5px dashed ${C.border}`, borderRadius:16, padding:22, textAlign:"center", marginBottom:16 }}>
              <div style={{ fontSize:32, marginBottom:12 }}>🔒</div>
              <div style={{ fontSize:16, fontWeight:800, marginBottom:8 }}>Buy to unlock full details</div>
              <p style={{ fontSize:13, color:"#AAAAAA", lineHeight:1.65, marginBottom:16 }}>Contact details, timeline, and booking readiness are hidden until you purchase. Once bought you can call or WhatsApp the customer directly.</p>
              <div style={{ display:"flex", gap:20, justifyContent:"center", flexWrap:"wrap" }}>
                {[["Name","Hidden"],["Phone","Hidden"],["Timeline","Hidden"]].map(([k,v])=>(
                  <div key={k} style={{ textAlign:"center" }}>
                    <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:"uppercase" }}>{k}</div>
                    <div style={{ filter:"blur(5px)", fontSize:14, fontWeight:700, color:C.text, userSelect:"none" }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <button onClick={()=>doBuy(l)} disabled={buying} style={{ width:"100%", background: buying?"#666":C.red, color:"#fff", border:"none", borderRadius:14, padding:16, fontFamily:"inherit", fontSize:16, fontWeight:800, cursor: buying?"not-allowed":"pointer", marginBottom:10 }}>
              {buying ? "Processing payment..." : `Buy this lead — £${l.price}`}
            </button>
            <div style={{ textAlign:"center", fontSize:12, color:C.muted }}>🔒 Secure payment via Stripe · No subscription required</div>
          </>);
        })()}
      </Sheet>

      {/* Purchased lead sheet */}
      <Sheet open={sheet?.type==="purchased"} onClose={()=>setSheet(null)}>
        {sheet?.type==="purchased"&&(()=>{
          const l=sheet.lead;
          const purchaseDate = l.createdAt?.toDate ? l.createdAt.toDate() : l.createdAt ? new Date(l.createdAt) : null;
          const dateStr = purchaseDate ? purchaseDate.toLocaleDateString("en-GB", {day:"numeric",month:"short",year:"numeric"}) : "";
          return (<>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
              <div style={{ fontSize:19, fontWeight:900 }}>{l.name}</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                <Badge color="blue">✓ Purchased</Badge>
                {(l.budget?.includes("1,000")||l.budget?.includes("5,000"))&&<Badge color="red">🔥 Hot</Badge>}
                {l.tier==="bronze"&&<Badge color="amber">🥉 Bronze</Badge>}
                {l.tier==="qualified"&&<Badge color="blue">🏠 Qualified</Badge>}
                {l.tier==="premium"&&<Badge color="green">⭐ Verified Premium</Badge>}
              </div>
            </div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:l.adminNote?12:20 }}>{l.vehicle} · {dateStr}</div>
            {l.adminNote&&<div style={{ background:"rgba(59,130,246,0.08)", border:"1px solid rgba(59,130,246,0.25)", borderRadius:10, padding:"10px 14px", marginBottom:16, fontSize:13, color:C.text, lineHeight:1.6 }}>💬 <strong>Expert note:</strong> {l.adminNote}</div>}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
              {[["Vehicle",l.vehicle],["Budget",l.budget],["Work type",l.workType],["Timeline",l.timeline],["Ready to book?",l.dropoff],["Best call time",l.callTime]].map(([k,v])=>(
                <div key={k}><Label>{k}</Label><div style={{ fontSize:13, color:C.text }}>{v}</div></div>
              ))}
              <div style={{ gridColumn:"1/-1" }}><Label>Damage description</Label><div style={{ fontSize:13, color:C.text, lineHeight:1.6 }}>{l.damage}</div></div>
            </div>
            {l.photoUrls?.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <Label>Photos</Label>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
                  {l.photoUrls.map((url,i)=>(<div key={i} style={{ aspectRatio:1, borderRadius:10, overflow:"hidden", border:`1px solid ${C.border}` }}><img src={url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /></div>))}
                </div>
              </div>
            )}
            <Divider />
            <div style={{ background:"rgba(22,163,74,0.06)", border:"1px solid rgba(22,163,74,0.2)", borderRadius:14, padding:16, marginBottom:14 }}>
              <div style={{ fontSize:10, textTransform:"uppercase", letterSpacing:"0.7px", color:C.greenLight, marginBottom:14, fontWeight:800 }}>✓ Contact details unlocked</div>
              {[["Full name",l.name],["Phone",l.phone],["WhatsApp",l.whatsapp||l.phone],["Email",l.email]].map(([k,v])=>(
                <div key={k} style={{ marginBottom:12 }}><div style={{ fontSize:10, color:C.greenLight, fontWeight:700, marginBottom:3 }}>{k}</div><div style={{ fontSize:16, fontWeight:800, color:C.greenLight }}>{v}</div></div>
              ))}
            </div>
            <a href={`https://wa.me/${(l.whatsapp||l.phone).replace(/\D/g,"")}`} style={{ width:"100%", background:"#25d366", color:"#fff", border:"none", borderRadius:14, padding:"15px", fontFamily:"inherit", fontSize:15, fontWeight:800, cursor:"pointer", marginBottom:10, display:"flex", alignItems:"center", justifyContent:"center", gap:8, textDecoration:"none" }}>💬 WhatsApp {l.name?.split(" ")[0]}</a>
            <a href={`tel:${l.phone}`} style={{ width:"100%", background:C.blue, color:"#fff", border:"none", borderRadius:14, padding:"15px", fontFamily:"inherit", fontSize:15, fontWeight:800, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, textDecoration:"none" }}>📞 Call {l.name?.split(" ")[0]} — {l.phone}</a>
          </>);
        })()}
      </Sheet>
    </div>
  );
}
